import { useQuery } from '@apollo/client';
import { GetSemesterPaymentPlansDocument } from '@sandtech-hub-monorepo/ehub-client';
import { Application, Enrollment } from '../types';
import { Button, Card } from '@sandtech-hub-monorepo/ui-kit';
import {
  EnrollmentPausedNotice,
  PaymentBannerMessages,
  PlannedCoursesView,
  ProgramEnrollmentPaymentBanner,
  ProgramEnrollmentStatusLabel,
} from '@sandtech-hub-monorepo/ui-kit-site';
import { ReactComponent as FitMark } from '../../../assets/Icons/fit-logo.svg';
import { AssistanceSection } from './AssistanceSection';
import { ApplicationRequirements } from './ApplicationRequirements';
import { ActiveConductCasesBanners } from './ActiveConductCasesBanners';
import { FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AppRoutesPaths } from '../../../routes/types';

const PAYMENT_BANNER_MESSAGES: PaymentBannerMessages = {
  success: (
    <>
      <b>Payment up to date:</b> Your fees are up to date, ensuring
      uninterrupted access to your program.
    </>
  ),
  fallback: {
    red: (
      <>
        <b>Payment required.</b> Please make a payment.
      </>
    ),
    amber: (
      <>
        <b>Payment due soon.</b> Please make a payment.
      </>
    ),
    blue: (
      <>
        <b>Payment coming up.</b> Please make a payment.
      </>
    ),
  },
  firstPaymentMade: {
    blue: ({ currentSemesterRemaining }) => (
      <>
        <b>First payment made:</b> Pay{' '}
        <b>${currentSemesterRemaining.toFixed(2)}</b> by the end of the semester
        to ensure uninterrupted access to your program.
      </>
    ),
  },
  beforeFirstWithOutstanding: {
    red: ({ instalmentAmount, previousSemesterRemaining, formattedOverdueDate }) => (
      <>
        <b>Payment overdue:</b> Make your first payment of{' '}
        <b>${instalmentAmount.toFixed(2)}</b> and clear outstanding balance{' '}
        <b>${previousSemesterRemaining.toFixed(2)}</b> by{' '}
        <b>{formattedOverdueDate}</b> to avoid being withdrawn from your
        program.
      </>
    ),
    amber: ({ instalmentAmount, previousSemesterRemaining, formattedDueDate }) => (
      <>
        <b>Payment due soon:</b> Make your first payment of{' '}
        <b>${instalmentAmount.toFixed(2)}</b> and clear outstanding balance{' '}
        <b>${previousSemesterRemaining.toFixed(2)}</b> by{' '}
        <b>{formattedDueDate}</b> to advance to the next term.
      </>
    ),
    blue: ({ instalmentAmount, previousSemesterRemaining, formattedDueDate }) => (
      <>
        <b>Payment coming up:</b> Make your first payment of{' '}
        <b>${instalmentAmount.toFixed(2)}</b> and clear outstanding balance{' '}
        <b>${previousSemesterRemaining.toFixed(2)}</b> by{' '}
        <b>{formattedDueDate}</b> to advance to the next term.
      </>
    ),
  },
  beforeFirst: {
    red: ({ formattedOverdueDate }) => (
      <>
        <b>Payment overdue:</b> Make your first payment by{' '}
        <b>{formattedOverdueDate}</b> to avoid being withdrawn from your
        program.
      </>
    ),
    amber: ({ formattedDueDate }) => (
      <>
        <b>Payment due soon:</b> Make your first payment by{' '}
        <b>{formattedDueDate}</b> to complete your enrollment and begin the
        term.
      </>
    ),
    blue: ({ formattedDueDate }) => (
      <>
        <b>Payment coming up:</b> Make your first payment by{' '}
        <b>{formattedDueDate}</b> to complete your enrollment and begin the
        term.
      </>
    ),
  },
};

interface Props {
  enrollment: Enrollment;
  application?: Application;
}

export const ApprovedApplicationView = ({ enrollment, application }: Props) => {
  const navigate = useNavigate();

  const { data: paymentPlansData } = useQuery(GetSemesterPaymentPlansDocument, {
    fetchPolicy: 'cache-first',
  });

  const paymentPlan = paymentPlansData?.viewer?.paymentPlans?.[0];

  const conductCases = enrollment.academicConductCases ?? [];

  const activeLeaveOfAbsence = enrollment.leavesOfAbsence?.find(
    (leave) => !leave.endedAt
  );
  const activeDeferral = enrollment.deferrals?.find(
    (deferral) => !deferral.endedAt
  );
  const isPaused = !!activeLeaveOfAbsence || !!activeDeferral;

  return (
    <>
      <Card className="px-6 py-9 border-heading">
        {/* Payment Banner */}
        {paymentPlan && (
          <div className="mb-6">
            <ProgramEnrollmentPaymentBanner
              paymentPlan={paymentPlan}
              paymentFAQsLink="/payments/faqs"
              messages={PAYMENT_BANNER_MESSAGES}
              showPaymentPlanName={false}
              showFAQLink={false}
            />
          </div>
        )}

        {/* Program Header Card */}
        <div className="border border-heading rounded-lg p-6 mb-6 flex flex-col gap-6">
          <div className="flex items-start gap-5">
            {/* Program Icon */}
            <div className="shrink-0">
              <FitMark className="w-[70px] h-[70px]" />
            </div>

            {/* Program Info */}
            <div className="flex-1 min-w-0 flex flex-col gap-2">
              <div className="flex gap-2">
                <h2 className="text-base font-semibold text-foreground">
                  {enrollment.program.name}
                </h2>
                <ProgramEnrollmentStatusLabel status={enrollment.status} />
                <div className="flex-1" />
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 gap-2 border-primary text-primary hover:bg-primary/5"
                  onClick={() => navigate(AppRoutesPaths.Transcript)}
                >
                  <FileText className="h-4 w-4" />
                  Preview Transcript
                </Button>
              </div>
              <p className="text-sm text-foreground/80">
                Your application for the {enrollment.program.name} has been
                approved!
                <br />
                You can now proceed to Frontier Institute of Technology.
              </p>
              {/* Academic Standing Banners */}
              <ActiveConductCasesBanners
                conductCases={conductCases}
                cumulativeGPA={enrollment?.cumulativeGPA}
              />
            </div>
          </div>
        </div>

        {/* Paused enrollment replaces the coursework — a learner on leave or
            deferred has no active courses to act on. */}
        {isPaused && (
          <EnrollmentPausedNotice
            kind={activeLeaveOfAbsence ? 'leaveOfAbsence' : 'deferral'}
            resumesOn={
              activeLeaveOfAbsence
                ? activeLeaveOfAbsence.expectedReturnDate
                : activeDeferral?.expectedReturnDate
            }
          />
        )}

        {/* Courses Section */}
        <div>
          {/* The list below is still rendered, so this cannot claim there are no
              courses — it explains why the ones shown cannot be acted on. */}
          {isPaused && (
            <p className="mb-3 text-sm text-foreground/80">
              These courses are paused while your enrollment is on hold:
            </p>
          )}
          <PlannedCoursesView
            plannedCourses={enrollment.plannedCourses}
            isUniversityProgram={enrollment.program.isUniversityProgram}
          />
        </div>
      </Card>

      {/* Application Requirements */}
      {application && (
        <Card className="px-6 py-3 border-heading">
          <ApplicationRequirements
            enrollment={enrollment}
            application={application}
          />
        </Card>
      )}

      {/* Assistance Section */}
      <AssistanceSection />
    </>
  );
};
